import { useEffect, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';

import { IEventStyle } from '@interfaces';
import { useAssetStore } from './assetsCasheContext';

const useAssetPreloader = ({
  appTheme,
  isAuthenticated,
}: {
  appTheme: IEventStyle | null;
  isAuthenticated: boolean;
}) => {
  const { addCachedAsset } = useAssetStore();

  const assets: null | string[] = useMemo(() => {
    if (!appTheme || !isAuthenticated) {
      return null;
    }

    const {
      newDefaultBackground,
      newDefaultBackgroundMobile,
      AvatarSelectionBackground,
      AvatarSelectionBackgroundMobile,
      DevicesTestMicrophoneBackground,
      DevicesTestSpeakerBackground,
      DevicesTestConnectionBackground,
      DevicesTestWebcamBackground,
      DevicesTestMicrophoneBackgroundMobile,
      DevicesTestSpeakerBackgroundMobile,
      DevicesTestConnectionBackgroundMobile,
      DevicesTestWebcamBackgroundMobile,
    } = appTheme;

    if (isMobileOnly) {
      return [
        newDefaultBackgroundMobile,
        AvatarSelectionBackgroundMobile,
        DevicesTestMicrophoneBackgroundMobile,
        DevicesTestSpeakerBackgroundMobile,
        DevicesTestConnectionBackgroundMobile,
        DevicesTestWebcamBackgroundMobile,
      ];
    }

    return [
      newDefaultBackground,
      AvatarSelectionBackground,
      DevicesTestMicrophoneBackground,
      DevicesTestSpeakerBackground,
      DevicesTestConnectionBackground,
      DevicesTestWebcamBackground,
    ];
  }, [appTheme, isAuthenticated]);

  useEffect(() => {
    if (!assets) {
      return;
    }

    let worker: Worker | null = null;

    if (window.Worker) {
      worker = new Worker(new URL('./assetLoader.worker.ts', import.meta.url));

      worker.onmessage = (e) => {
        const { type, url, blob } = e.data;

        if (type === 'ASSET_LOADED') {
          //later URL.revokeObjectURL(blobUrl) called
          addCachedAsset(url, URL.createObjectURL(blob as Blob) as string);
        }

        if (type === 'LOADING_FINISHED') {
          worker?.terminate();
        }
      };

      worker.postMessage(assets);
    }

    return () => {
      if (worker) {
        worker.terminate();
      }
    };
  }, [assets, addCachedAsset]);
};

export default useAssetPreloader;
